import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { media, screenSizeBreakpoints, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { ButtonText } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { popoverAnchorGap } from "@iventis/styles/src/atomic-rules";
import { StyledInteractiveArea } from "@iventis/styles/src/components";
import React, { BaseSyntheticEvent, createElement, FunctionComponent, memo, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material";
import useOnScreen from "@iventis/utilities/src/hooks/use-on-screen";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Level, MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { SavedMapView } from "@iventis/domain-model/model/savedMapView";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { unwrapResult } from "@reduxjs/toolkit";
import { EMPTY_GUID, useFunctionality } from "@iventis/utilities";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { toast } from "@iventis/toasts/src/toast";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { getSelectedSitemaps } from "./map.helpers";
import { sidebar } from "./state/sidebar.slice";
import { SidebarState, PopoverComponents, FullScreenSelector } from "./state/sidebar.slice.types";
import { popoverComponents } from "./popover-list";
import { createMapPopoverProps } from "./map-container.helpers";
import { classNameTourSearch, MapTourContext, MapTourStep } from "./map-tour";
import { MapNavigationTooltip } from "./map-navigation-tooltip";
import { map } from "./state/map.slice";
import { RootState, rootStore } from "./state/root.store";
import { createSavedMapView, patchDefaultSavedMapView } from "./state/map.slice.thunks";
import { useGetLayerThumbnailPreview } from "./hooks/use-layer-thumbnail-preview";

const generateDisplayTextForSitemaps = (sitemaps: Sitemap[] = [], sitemapConfigs: MapSitemapConfig[] = []): { text: string; number: number } => {
    const selected = getSelectedSitemaps(sitemaps, sitemapConfigs);
    const sitemapsDisplayed = {
        text: "",
        number: selected.length,
    };

    switch (selected.length) {
        case 0: {
            if (sitemaps.length > 0) {
                sitemapsDisplayed.text = "No sitemap selected";
            } else {
                sitemapsDisplayed.text = "No sitemap here";
            }
            break;
        }
        case 1: {
            sitemapsDisplayed.text = selected[0].name;
            break;
        }
        default: {
            sitemapsDisplayed.text = "Multiple sitemaps";
            break;
        }
    }
    return sitemapsDisplayed;
};

const getDisplayTextForLevel = (selectedLevelIndex: number, levels: Level[], showFullName = false): string => {
    const selectedLevel = levels.find((l) => l.index === selectedLevelIndex);
    if (!selectedLevel) {
        return "None";
    }
    if (showFullName) {
        return selectedLevel.name;
    }
    return selectedLevel.abbreviation;
};

const MapNavigationComponent: FunctionComponent<{
    setPopoverAnchor: (anchor: Element) => void;
    windowWidth: number;
    showTooltip: boolean;
    mapId?: string;
}> = ({ setPopoverAnchor, windowWidth, showTooltip, mapId }) => {
    const dispatch = useDispatch();

    const translate = useIventisTranslate();

    const functionality = useFunctionality();

    const { ref: mapTourRef } = useContext(MapTourContext);

    const [showSitemapSelector, setShowSitemapSelector] = useState(false);

    const getMapLayerPreviewUrl = useGetLayerThumbnailPreview();

    const handleMapTour = () => {
        // Once we have saved our map view and we have the tour running on the background step, reopen it
        if (
            mapTourRef?.current &&
            mapTourRef.current.isRunning() &&
            (mapTourRef.current.getStep() === MapTourStep.LOCATION || mapTourRef.current.getStep() === MapTourStep.BACKGROUND)
        ) {
            mapTourRef.current.maximiseTour();

            // We may have already progressed to the next step depending if we clicked through the spotlight or not
            if (mapTourRef.current.getStep() === MapTourStep.LOCATION) mapTourRef.current.nextStep();
        }
    };

    const sitemapRef = useRef<HTMLDivElement>(null);
    const levelRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);
    const selectorRefs = {
        [PopoverComponents.SITEMAP_SELECTOR]: sitemapRef,
        [PopoverComponents.LEVEL_SELECTOR]: levelRef,
        [PopoverComponents.SEARCH]: searchRef,
    };

    const theme = useTheme<Theme>();

    const { levelAbbreviation, sitemapsDisplayed, showSavedMapTooltip } = useSelector(
        ({ mapReducer }: { mapReducer: MapGlobalState }) => ({
            levelAbbreviation: getDisplayTextForLevel(mapReducer.mapModule.currentLevel, mapReducer.levels),
            sitemapsDisplayed: generateDisplayTextForSitemaps(mapReducer.sitemapsInView, mapReducer.sitemapConfigs),
            showSavedMapTooltip: mapReducer.savedMapTooltip,
        }),
        (prev, next) =>
            prev.levelAbbreviation === next.levelAbbreviation &&
            prev.sitemapsDisplayed.text === next.sitemapsDisplayed.text &&
            prev.showSavedMapTooltip === next.showSavedMapTooltip
    );

    const { fullScreenSelector } = useSelector(({ sidebarReducer }: { sidebarReducer: SidebarState }) => sidebarReducer);

    useEffect(() => {
        if (windowWidth > screenSizeBreakpoints.medium && fullScreenSelector) {
            dispatch(sidebar.actions.setFullScreenSelector(null));
            setPopoverAnchor(selectorRefs[fullScreenSelector].current);
            setPopover(PopoverComponents[fullScreenSelector]);
        }
    }, [windowWidth]);

    const setPopover = (component: FullScreenSelector) => {
        dispatch(
            sidebar.actions.setPopover({
                component,
                props: {
                    ...createMapPopoverProps(component, mapId, { getMapLayerPreviewUrl }),
                    onClose: () => {
                        if (component === PopoverComponents.SEARCH) handleMapTour();
                    },
                },
                nodeId: null,
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                },
                translateY: `-${popoverAnchorGap}`,
            })
        );
    };

    const openSelector = (element: Element, component: FullScreenSelector) => {
        // If user is on mobile device, anchor to the mapRoot as it needs to cover the entire map
        if (windowWidth <= screenSizeBreakpoints.medium) {
            dispatch(sidebar.actions.setFullScreenSelector(component));
        } else {
            setPopoverAnchor(element);
            setPopover(component);
        }
    };

    const [popupDismissed, setPopupDismissed] = useState(false);

    const elementRef = useRef(null);
    let isOnScreen = useOnScreen(elementRef);

    useEffect(() => {
        if (mapTourRef?.current && mapTourRef.current.isRunning() && mapTourRef.current.getStep() === MapTourStep.LOCATION) {
            // check if the element we are targetting is rendered on the screen
            if (isOnScreen) {
                // if so we can maximise the tour to ensure it renders in the correct location
                mapTourRef.current.maximiseTour();
                // reset isOnScreen as we dont need to look for the element any more
                isOnScreen = null;
            } else {
                // otherwise we want to minimise it to prevent it rendering in the wrong place
                mapTourRef.current.minimiseTour();
            }
        }
    }, [isOnScreen, mapTourRef]);

    return (
        <>
            {fullScreenSelector && (
                <StyledFullScreenSelector>
                    {createElement(popoverComponents[fullScreenSelector], createMapPopoverProps(fullScreenSelector, mapId, { getMapLayerPreviewUrl }))}
                </StyledFullScreenSelector>
            )}
            <StyledMapNavigation>
                <MapNavigationTooltip
                    showTooltip={showSavedMapTooltip && !popupDismissed && showTooltip}
                    onClose={() => {
                        dispatch(map.actions.setSavedMapViewTooltip(false));
                        setPopupDismissed(true);
                        handleMapTour();
                    }}
                    onSave={async () => {
                        const { mapReducer } = rootStore.getState() as RootState;
                        const { position, mapId, currentLevel } = mapReducer.mapModule;
                        const mapView: SavedMapView = {
                            id: undefined,
                            name: "Default Map View",
                            shortName: "",
                            position: {
                                latitude: position.value.lat,
                                longitude: position.value.lng,
                                zoom: position.value.zoom,
                                bearing: position.value.bearing,
                                pitch: position.value.pitch,
                            },
                            mapId,
                            projectId: EMPTY_GUID,
                            level: currentLevel,
                        };
                        const payload = unwrapResult(await rootStore.dispatch(createSavedMapView(mapView)));
                        mapView.id = payload.id;

                        await rootStore.dispatch(patchDefaultSavedMapView({ mapId, savedMapViewId: mapView.id }));
                    }}
                >
                    <StyledSearchSelector ref={searchRef}>
                        <StyledInteractiveArea
                            data-testid="map-search-button"
                            className={classNameTourSearch}
                            zIndex={50}
                            aria-label="Search"
                            type="button"
                            ref={elementRef}
                            onClick={(e: BaseSyntheticEvent) => {
                                openSelector(e.currentTarget, PopoverComponents.SEARCH);
                                incrementMonitoringCount({ name: MetricName.PLAN_SEARCHED });
                                // If we're on the map tour and its running and we're on the set location step then minimise it and progress the tour if we click the search icon
                                if (mapTourRef?.current && mapTourRef.current.isRunning() && mapTourRef.current.getStep() === MapTourStep.LOCATION) {
                                    mapTourRef.current.minimiseTour();
                                }
                            }}
                        >
                            <span className="hidden">{translate(Content.map.nav.search)}</span>
                        </StyledInteractiveArea>
                        <FontAwesomeIcon style={{ padding: "0px 5px" }} icon={["far", "magnifying-glass"]} />
                    </StyledSearchSelector>
                </MapNavigationTooltip>
                {functionality.siteMaps && (
                    <IventisErrorBoundaryWithToast toast={toast}>
                        <StyledSitemapSelector>
                            {windowWidth < screenSizeBreakpoints.medium && (
                                <StyledMobileInteractiveArea
                                    zIndex={50}
                                    aria-label="Mobile Sitemap selector"
                                    type="button"
                                    onClick={() => setShowSitemapSelector(!showSitemapSelector)}
                                    data-testid="mobile-sitemap-selector-toggle"
                                >
                                    <FontAwesomeIcon style={{ padding: "0px 7px" }} size="lg" icon={[showSitemapSelector ? "fas" : "far", "location-dot"]} />
                                </StyledMobileInteractiveArea>
                            )}

                            <StyledSitemapSelectorWrapper $show={windowWidth >= screenSizeBreakpoints.medium || showSitemapSelector}>
                                <div ref={sitemapRef} className="selector sitemap">
                                    <StyledInteractiveArea
                                        zIndex={50}
                                        aria-label="Sitemap selector"
                                        type="button"
                                        onClick={(e: BaseSyntheticEvent) => {
                                            incrementMonitoringCount({ name: MetricName.SITEMAP_SELECTOR_USED });
                                            openSelector(e.currentTarget, PopoverComponents.SITEMAP_SELECTOR);
                                        }}
                                        data-testid="sitemap-selector-button"
                                    >
                                        <span className="hidden">{translate(Content.map.nav.selectSitemap)}</span>
                                    </StyledInteractiveArea>
                                    <ButtonText
                                        style={{
                                            opacity: sitemapsDisplayed.number > 1 ? 0.7 : 0.5,
                                            color: theme.typographyColors.core,
                                            fontStyle: sitemapsDisplayed.number > 1 ? "italic" : "normal",
                                        }}
                                        data-testid="sitemap-displayed-test"
                                    >
                                        {sitemapsDisplayed.text}
                                    </ButtonText>
                                    <FontAwesomeIcon icon="sort-down" />
                                </div>
                                <div ref={levelRef} className="selector">
                                    <StyledInteractiveArea
                                        zIndex={50}
                                        aria-label="Level selector"
                                        type="button"
                                        onClick={(e: BaseSyntheticEvent) => {
                                            incrementMonitoringCount({ name: MetricName.SITEMAP_LEVEL_USED });
                                            openSelector(e.currentTarget, PopoverComponents.LEVEL_SELECTOR);
                                        }}
                                        data-testid="sitemap-level-selector-button"
                                    >
                                        <span className="hidden">{translate(Content.map.nav.selectLevel)}</span>
                                    </StyledInteractiveArea>
                                    <ButtonText style={{ opacity: 0.7 }}>{levelAbbreviation}</ButtonText>
                                    <FontAwesomeIcon icon="sort-down" />
                                </div>
                            </StyledSitemapSelectorWrapper>
                        </StyledSitemapSelector>
                    </IventisErrorBoundaryWithToast>
                )}
            </StyledMapNavigation>
        </>
    );
};

const StyledFullScreenSelector = styled.div`
    position: absolute;
    top: 56px;
    left: 0;
    width: 100%;
    height: calc(100% - 56px);
    background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
    z-index: 500;
`;

const StyledMapNavigation = styled.div`
    position: absolute;
    bottom: 40px;
    left: 10px;
    display: flex;
    height: 40px;

    ${media.extraExtraSmall} {
        left: 5px;
    }

    ${media.small} {
        left: 30px;
    }
`;

const StyledSitemapSelector = styled.div`
    display: flex;
    height: 100%;
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 0 5px 1px ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    .selector {
        position: relative;
        border: solid 1px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0 10px;
        align-items: center;
        svg {
            margin-left: 10px;
            margin-bottom: 5px;
            color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        }
    }
    .sitemap {
        margin-right: 6px;
        min-width: 200px;
    }
`;

const StyledSearchSelector = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    position: relative;
    padding: 4px 8px;
    border-radius: 4px;
    box-shadow: 0 0 5px 1px ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    svg {
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    }
    margin-right: 10px;

    ${media.extraExtraSmall} {
        margin-right: 5px;
    }
`;

const StyledSitemapSelectorWrapper = styled.div<{ $show: boolean }>`
    display: ${({ $show }) => ($show ? "flex" : "none")};
    width: 0;

    .selector {
        position: absolute;
        bottom: 55px;
        left: -87px;
        display: flex;
        align-items: center;
        height: 100%;
        position: relative;
        padding: 4px 8px;
        border-radius: 4px;
        border: 1px solid ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.1);
        background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
        svg {
            color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        }

        ${media.extraExtraSmall} {
            left: -82px;
        }

        ${media.medium} {
            position: relative;
            bottom: auto;
            left: auto;
            border: 1px solid rgba(0, 0, 0, 0.1);
            box-shadow: none;
        }
    }

    ${media.medium} {
        width: auto;
    }
`;

const StyledMobileInteractiveArea = styled(StyledInteractiveArea)`
    position: relative;
    svg {
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    }
`;
const MemoizedMapNavigationComponent = memo(MapNavigationComponent);
export default MemoizedMapNavigationComponent;
